import React, { useEffect, useMemo, useState } from 'react';
import DeliveryPhotoLocationPanel, { type DeliveryPhotoLocationPanelProps } from './DeliveryPhotoLocationPanel';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';

export interface DeliveryImagePreviewDetail {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
}

interface DeliveryImagePreviewDialogProps {
  open: boolean;
  imageUrl: string;
  title: string;
  filename?: string;
  details?: DeliveryImagePreviewDetail[];
  locationAudit?: DeliveryPhotoLocationPanelProps;
  onClose: () => void;
}

const MIN_ZOOM = 50;
const MAX_ZOOM = 300;
const ZOOM_STEP = 25;

const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const DeliveryImagePreviewDialog: React.FC<DeliveryImagePreviewDialogProps> = ({
  open,
  imageUrl,
  title,
  filename,
  details = [],
  locationAudit,
  onClose,
}) => {
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    if (open) setZoom(100);
  }, [imageUrl, open]);

  const visibleDetails = useMemo(
    () => details.filter((detail) => detail.value !== undefined && detail.value !== null && detail.value !== ''),
    [details],
  );
  const hasSidebar = visibleDetails.length > 0 || !!locationAudit;

  const handleDownload = () => {
    if (!imageUrl) return;

    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'delivery-image.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          bgcolor: '#080a0d',
          color: 'common.white',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          py: 1,
          px: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          bgcolor: '#0d1117',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {title || 'ตัวอย่างรูปภาพ'}
            </Typography>
            {filename && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.68)',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {filename}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
            <Tooltip title="ย่อรูป">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setZoom((value) => clampZoom(value - ZOOM_STEP))}
                  disabled={zoom <= MIN_ZOOM}
                  sx={{ color: 'common.white', border: '1px solid rgba(255, 255, 255, 0.18)' }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setZoom(100)}
              sx={{
                minWidth: 68,
                color: 'common.white',
                borderColor: 'rgba(255, 255, 255, 0.28)',
                '&:hover': { borderColor: 'rgba(255, 255, 255, 0.48)' },
              }}
            >
              100%
            </Button>
            <Tooltip title="ขยายรูป">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setZoom((value) => clampZoom(value + ZOOM_STEP))}
                  disabled={zoom >= MAX_ZOOM}
                  sx={{ color: 'common.white', border: '1px solid rgba(255, 255, 255, 0.18)' }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Chip
              label={`${zoom}%`}
              size="small"
              sx={{
                color: 'common.white',
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                minWidth: 58,
                fontWeight: 700,
              }}
            />
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!imageUrl}
              sx={{ minWidth: 112 }}
            >
              ดาวน์โหลด
            </Button>
            <Tooltip title="ปิด">
              <IconButton
                size="small"
                onClick={onClose}
                sx={{ color: 'common.white', border: '1px solid rgba(255, 255, 255, 0.18)' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: hasSidebar ? 'minmax(0, 1fr) 360px' : '1fr' },
          gridTemplateRows: { xs: hasSidebar ? 'minmax(0, 1fr) minmax(150px, auto)' : '1fr', md: '1fr' },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflow: 'auto',
            bgcolor: '#020303',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minWidth: zoom > 100 ? `${zoom}%` : '100%',
              minHeight: zoom > 100 ? `${zoom}%` : '100%',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={title}
              sx={{
                maxWidth: zoom <= 100 ? `${zoom}%` : '100%',
                maxHeight: zoom <= 100 ? `${zoom}%` : '100%',
                objectFit: 'contain',
                display: 'block',
                borderRadius: 1,
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.48)',
              }}
            />
          </Box>
        </Box>

        {hasSidebar && (
          <Box
            sx={{
              minHeight: 0,
              overflowY: 'auto',
              bgcolor: '#10161f',
              borderLeft: { xs: 'none', md: '1px solid rgba(255, 255, 255, 0.12)' },
              borderTop: { xs: '1px solid rgba(255, 255, 255, 0.12)', md: 'none' },
              maxHeight: { xs: '32vh', md: 'none' },
              p: 1.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              ข้อมูลรูปภาพ
            </Typography>
            <Stack divider={<Divider flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />}>
              {visibleDetails.map((detail, index) => (
                <Box key={`${detail.label}-${index}`} sx={{ py: 0.9 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.56)' }}>
                    {detail.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      mt: 0.25,
                      color: 'rgba(255, 255, 255, 0.9)',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                      fontFamily: detail.mono ? 'monospace' : undefined,
                    }}
                  >
                    {detail.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
            {locationAudit && open && (
              <Box sx={{ mt: 1.5 }}>
                <DeliveryPhotoLocationPanel {...locationAudit} />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeliveryImagePreviewDialog;
